/**
 * Shared API client for all features.
 *
 * Exports:
 *   apiClient  — JSON requests with auth + 401 retry
 *   apiUpload  — FormData uploads with auth + 401 retry (no Content-Type override)
 *   authHeader — bare { Authorization } dict for one-off needs (SSE, etc.)
 *   buildQueryString — utility
 *
 * Wire contract: the server emits bare record keys (no "table:" prefix).
 * Clients store bare keys and send them back verbatim. No ID normalization
 * on this side — if a response ever contains joined form, fix the server.
 */

import {
  getAccessToken,
  getSessionOwnerId,
  setAccessToken,
  clearAccessToken,
  forceSignOut,
} from "@/features/auth/lib/session";
import { refreshToken as doRefresh, NetworkError } from "@/features/auth/api";
import type { User } from "@/features/auth/types";

export const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080";

// ===================
// Error
// ===================

/**
 * The parsed form of a typed server error body.
 *
 * Mirrors `FinanceError.to_detail()` in `backend/src/finance/errors.py`. Every
 * field beyond `code`/`message` is additive: a route that has not adopted the
 * taxonomy yet simply leaves them null, and nothing downstream changes shape.
 */
export interface ParsedErrorBody {
  message: string;
  code: string | null;
  /** Whether retrying the same request could plausibly succeed. */
  retryable: boolean | null;
  /** Who can act: "user" gets a button, "operator" an alert, "provider" a wait. */
  actionableBy: string | null;
  /** Structured facts (ids, counts, offending field names) — never prose. */
  context: Record<string, unknown> | null;
  /** The upstream system's own verbatim code, e.g. Plaid's ITEM_LOGIN_REQUIRED. */
  providerCode: string | null;
  /** Closed-loop correlation id — joins this failure to backend logs/traces. */
  requestId: string | null;
}

export class ApiError extends Error {
  /** Branchable server error code when the body carried one
   *  (`{"detail": {"code", …}}` — the typed envelope), else null. */
  public code: string | null;
  public retryable: boolean | null;
  public actionableBy: string | null;
  public context: Record<string, unknown> | null;
  /** Upstream's own diagnosis. This is the field that distinguishes
   *  "reconnect the bank" from "the bank is having an outage". */
  public providerCode: string | null;
  public requestId: string | null;

  constructor(
    public status: number,
    public statusText: string,
    body: string,
  ) {
    const parsed = parseErrorBody(body);
    super(parsed.message || statusText || `HTTP ${status}`);
    this.name = "ApiError";
    this.code = parsed.code;
    this.retryable = parsed.retryable;
    this.actionableBy = parsed.actionableBy;
    this.context = parsed.context;
    this.providerCode = parsed.providerCode;
    this.requestId = parsed.requestId;
  }
}

const EMPTY_TYPED_FIELDS = {
  retryable: null,
  actionableBy: null,
  context: null,
  providerCode: null,
  requestId: null,
} as const;

/**
 * Turn a raw error body into something a toast can show AND branch on.
 * Handles the three shapes this backend emits: `{"detail": {"code", …}}`
 * (typed service errors), `{"detail": "text"}` (HTTPException), and
 * `{"detail": [{loc, msg}, …]}` (422 validation). Anything else is
 * returned verbatim — better an ugly true message than a hidden one.
 */
function parseErrorBody(body: string): ParsedErrorBody {
  if (!body) return { message: "", code: null, ...EMPTY_TYPED_FIELDS };
  try {
    const json = JSON.parse(body) as { detail?: unknown };
    const d = json.detail;
    if (typeof d === "string") {
      return { message: d, code: null, ...EMPTY_TYPED_FIELDS };
    }
    if (Array.isArray(d)) {
      const msgs = d
        .map((e) => {
          const err = e as { loc?: unknown[]; msg?: string };
          const loc = Array.isArray(err.loc) ? err.loc.slice(1).join(".") : "";
          return loc ? `${loc}: ${err.msg ?? ""}` : (err.msg ?? "");
        })
        .filter(Boolean);
      return {
        message: msgs.join("; ") || "Validation failed",
        code: "validation",
        ...EMPTY_TYPED_FIELDS,
      };
    }
    if (d && typeof d === "object") {
      const obj = d as {
        code?: string;
        message?: string;
        retryable?: boolean;
        actionable_by?: string;
        context?: Record<string, unknown>;
        provider_code?: string;
        request_id?: string;
      };
      return {
        message: obj.message ?? JSON.stringify(d),
        code: obj.code ?? null,
        retryable: typeof obj.retryable === "boolean" ? obj.retryable : null,
        actionableBy: obj.actionable_by ?? null,
        context: obj.context ?? null,
        providerCode: obj.provider_code ?? null,
        requestId: obj.request_id ?? null,
      };
    }
  } catch {
    /* not JSON — fall through */
  }
  return { message: body, code: null, ...EMPTY_TYPED_FIELDS };
}

// ===================
// Types
// ===================

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

// ===================
// Header helpers
// ===================

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/New_York";
  }
}

function jsonHeaders(extra?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Timezone": getBrowserTimezone(),
  };
  const token = getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  mergeExtra(headers, extra);
  return headers;
}

export function authHeader(): Record<string, string> {
  const headers: Record<string, string> = {
    "X-Timezone": getBrowserTimezone(),
  };
  const token = getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

function mergeExtra(headers: Record<string, string>, extra?: HeadersInit) {
  if (!extra) return;
  const entries =
    extra instanceof Headers
      ? Array.from(extra.entries())
      : Array.isArray(extra)
        ? extra
        : Object.entries(extra);
  for (const [key, value] of entries) {
    headers[key] = value;
  }
}

// ===================
// Token refresh (singleton to deduplicate concurrent 401s)
// ===================

interface RefreshOutcome {
  ok: boolean;
  /**
   * The user payload from the backend's /auth/refresh response —
   * lets useAuth.hydrate() skip the second /auth/me roundtrip on
   * cold page loads. Cuts cold-auth from 2 backend calls to 1.
   */
  user: User | null;
}

let refreshPromise: Promise<RefreshOutcome> | null = null;

/**
 * Attempt refresh. Module-level singleton dedupes concurrent 401s
 * within a single tab — the first 401 starts a refresh, the next N
 * 401s in the same tab await the same promise.
 *
 * RE-THROWS NetworkError so callers can distinguish "server unreachable"
 * from "session expired" and render the right UI.
 *
 * Returns { ok, user } so callers that need the user object (hydrate)
 * don't have to make a separate /auth/me call. Cross-tab single-flight
 * isn't needed — the backend's 30-second concurrent-grace window in
 * auth/service.py:refresh() catches the cross-tab race.
 */
export async function tryRefresh(): Promise<RefreshOutcome> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async (): Promise<RefreshOutcome> => {
    try {
      const result = await doRefresh();
      setAccessToken(result.access_token, result.expires_in, result.user?.id);
      return { ok: true, user: result.user ?? null };
    } catch (err) {
      if (err instanceof NetworkError) {
        // Don't clear the access token — we can't tell if the refresh
        // would have succeeded; UI surfaces a "server unreachable" screen.
        throw err;
      }
      clearAccessToken();
      return { ok: false, user: null };
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// ===================
// Core: handle 401 retry for any response
// ===================

async function handleResponse<T>(
  res: Response,
  retryFetch: () => Promise<Response>,
): Promise<T> {
  if (res.status === 401) {
    const { ok } = await tryRefresh();
    if (ok) {
      const retryRes = await retryFetch();
      if (!retryRes.ok) {
        const errorText = await retryRes.text();
        throw new ApiError(retryRes.status, retryRes.statusText, errorText);
      }
      if (retryRes.status === 204) return undefined as T;
      return (await retryRes.json()) as T;
    }
    // Refresh failed — token revoked, replay detected, or expired.
    // Funnel through forceSignOut so the redirect-to-login behavior is
    // identical across the three call sites that can trigger it.
    forceSignOut("token-revoked");
    throw new ApiError(401, "Unauthorized", "Session expired");
  }

  if (!res.ok) {
    const errorText = await res.text();
    throw new ApiError(res.status, res.statusText, errorText);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ===================
// apiClient — JSON requests
// ===================

export async function apiClient<T>(
  endpoint: string,
  options?: RequestInit & { expectedOwnerId?: string },
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const { expectedOwnerId, ...requestOptions } = options ?? {};

  const doFetch = () => {
    if (expectedOwnerId && getSessionOwnerId() !== expectedOwnerId) {
      throw new ApiError(401, "Unauthorized", "Sign in to the account that owns this draft to save it.");
    }
    return fetch(url, {
      ...requestOptions,
      credentials: "include",
      headers: jsonHeaders(requestOptions.headers),
    });
  };

  const res = await doFetch();
  return handleResponse<T>(res, doFetch);
}

// ===================
// apiUpload — FormData uploads (no Content-Type — browser sets boundary)
// ===================

export async function apiUpload<T>(
  endpoint: string,
  body: FormData,
  options?: { method?: string; signal?: AbortSignal; headers?: Record<string, string> },
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const doFetch = () =>
    fetch(url, {
      method: options?.method ?? "POST",
      body,
      credentials: "include",
      headers: { ...authHeader(), ...options?.headers },
      signal: options?.signal,
    });

  const res = await doFetch();
  return handleResponse<T>(res, doFetch);
}

// ===================
// Utilities
// ===================

export function buildQueryString(
  params: Record<string, string | number | boolean | string[] | undefined>,
): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v));
    } else {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}
